import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { AuditLogSetting } from '../../types/admin';
import { auditSettingsApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';

/**
 * Gộp các entity_type "số ít" (dùng bởi hầu hết action) và "số nhiều" (tên bảng
 * thật, dùng bởi SOFT_DELETE — xem migration 0003) về cùng một nhóm hiển thị,
 * để Admin thấy đúng logic nghiệp vụ thay vì chi tiết lưu trữ.
 */
const ENTITY_GROUP_META: Record<string, { group: string; groupLabel: string }> = {
  project: { group: 'project', groupLabel: 'Dự án' },
  projects: { group: 'project', groupLabel: 'Dự án' },
  project_users: { group: 'project', groupLabel: 'Dự án' },
  phase: { group: 'phase', groupLabel: 'Giai đoạn' },
  phases: { group: 'phase', groupLabel: 'Giai đoạn' },
  feature: { group: 'feature', groupLabel: 'Tính năng' },
  features: { group: 'feature', groupLabel: 'Tính năng' },
  feature_image: { group: 'feature_image', groupLabel: 'Hình ảnh tính năng' },
  feature_images: { group: 'feature_image', groupLabel: 'Hình ảnh tính năng' },
  change_request: { group: 'change_request', groupLabel: 'Yêu cầu thay đổi' },
  role: { group: 'role', groupLabel: 'Vai trò' },
  roles: { group: 'role', groupLabel: 'Vai trò' },
  role_permissions: { group: 'role', groupLabel: 'Vai trò' },
  role_feature_status_filters: { group: 'role', groupLabel: 'Vai trò' },
  user: { group: 'user', groupLabel: 'Người dùng' },
  users: { group: 'user', groupLabel: 'Người dùng' },
  user_roles: { group: 'user', groupLabel: 'Người dùng' },
};

const ACTION_LABELS: Record<string, string> = {
  SOFT_DELETE: 'Xóa mềm',
  CREATE: 'Tạo mới',
  UPDATE: 'Cập nhật',
  UPDATE_STATUS: 'Đổi trạng thái hoạt động',
  ROLE_ASSIGN: 'Gán vai trò',
  ROLE_REMOVE: 'Gỡ vai trò',
  ROLE_PERMISSION_ASSIGN: 'Gán quyền cho vai trò',
  ROLE_PERMISSION_REMOVE: 'Gỡ quyền khỏi vai trò',
  ROLE_FEATURE_STATUS_FILTER_SET: 'Đặt bộ lọc trạng thái tính năng',
  LOGIN: 'Đăng nhập',
  LOGOUT: 'Đăng xuất',
  PROJECT_CREATE: 'Tạo dự án',
  PROJECT_UPDATE: 'Cập nhật dự án',
  PROJECT_USER_ASSIGN: 'Gán thành viên vào dự án',
  PROJECT_USER_REMOVE: 'Gỡ thành viên khỏi dự án',
  PHASE_CREATE: 'Tạo giai đoạn',
  PHASE_UPDATE: 'Cập nhật giai đoạn',
  FEATURE_CREATE: 'Tạo tính năng',
  FEATURE_BULK_CREATE: 'Import hàng loạt tính năng',
  FEATURE_UPDATE: 'Cập nhật tính năng',
  FEATURE_CONFIRM: 'Xác nhận nghiệm thu tính năng',
  FEATURE_IMAGE_CREATE: 'Tải lên hình ảnh',
  CHANGE_REQUEST_CREATE: 'Tạo yêu cầu thay đổi',
  CHANGED_REQUEST_UPDATE: 'Cập nhật xử lý yêu cầu thay đổi',
  CHANGED_REQUEST_RESOLVE: 'Xử lý xong yêu cầu thay đổi',
};

/**
 * Nhãn ưu tiên cho các cặp (entity_type, action) dễ gây nhầm lẫn khi gộp
 * nhóm — chủ yếu là SOFT_DELETE trên nhiều bảng khác nhau bị gộp chung một
 * nhóm nghiệp vụ (vd "Vai trò" gồm cả roles/role_permissions/
 * role_feature_status_filters), nếu chỉ nhìn action thì đều ra "Xóa mềm".
 */
const ENTITY_ACTION_LABELS: Record<string, string> = {
  'projects SOFT_DELETE': 'Xóa mềm dự án',
  'project_users SOFT_DELETE': 'Xóa mềm thành viên dự án',
  'phases SOFT_DELETE': 'Xóa mềm giai đoạn',
  'features SOFT_DELETE': 'Xóa mềm tính năng',
  'feature_images SOFT_DELETE': 'Xóa mềm hình ảnh',
  'roles SOFT_DELETE': 'Xóa mềm vai trò',
  'role_permissions SOFT_DELETE': 'Xóa mềm gán quyền vai trò',
  'role_feature_status_filters SOFT_DELETE': 'Xóa mềm bộ lọc trạng thái tính năng',
  'users SOFT_DELETE': 'Xóa mềm người dùng',
  'user_roles SOFT_DELETE': 'Xóa mềm gán vai trò người dùng',
};

function actionLabel(entityType: string, action: string): string {
  return ENTITY_ACTION_LABELS[`${entityType} ${action}`] ?? ACTION_LABELS[action] ?? action;
}

interface GroupedSettings {
  group: string;
  groupLabel: string;
  rows: AuditLogSetting[];
}

/** Cấu hình bật/tắt ghi nhật ký hệ thống, gộp nhóm theo loại đối tượng nghiệp vụ. */
export function AuditSettings(): JSX.Element {
  const [settings, setSettings] = useState<AuditLogSetting[]>([]);
  const [edited, setEdited] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const key = (entityType: string, action: string) => `${entityType} ${action}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await auditSettingsApi.list();
      setSettings(list);
      setEdited(Object.fromEntries(list.map((s) => [key(s.entity_type, s.action), s.enabled !== 0])));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải cấu hình nhật ký'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo<GroupedSettings[]>(() => {
    const byGroup = new Map<string, GroupedSettings>();
    for (const s of settings) {
      const meta = ENTITY_GROUP_META[s.entity_type] ?? { group: s.entity_type, groupLabel: s.entity_type };
      const existing = byGroup.get(meta.group);
      if (existing) {
        existing.rows.push(s);
      } else {
        byGroup.set(meta.group, { group: meta.group, groupLabel: meta.groupLabel, rows: [s] });
      }
    }
    return Array.from(byGroup.values()).sort((a, b) => a.groupLabel.localeCompare(b.groupLabel, 'vi'));
  }, [settings]);

  const isRowEnabled = (s: AuditLogSetting) => edited[key(s.entity_type, s.action)] ?? s.enabled !== 0;

  const toggleRow = (s: AuditLogSetting) => {
    setEdited((prev) => ({ ...prev, [key(s.entity_type, s.action)]: !isRowEnabled(s) }));
  };

  const toggleGroup = (group: GroupedSettings, value: boolean) => {
    setEdited((prev) => {
      const next = { ...prev };
      for (const row of group.rows) next[key(row.entity_type, row.action)] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = settings.map((s) => ({
        entity_type: s.entity_type,
        action: s.action,
        enabled: isRowEnabled(s),
      }));
      const updated = await auditSettingsApi.update(payload);
      setSettings(updated);
      setEdited(Object.fromEntries(updated.map((s) => [key(s.entity_type, s.action), s.enabled !== 0])));
      notify('Đã lưu cấu hình nhật ký', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu cấu hình nhật ký'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Cấu hình nhật ký hệ thống
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bật/tắt việc ghi nhật ký theo từng hành động, gộp nhóm theo loại đối tượng.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Tải lại">
            <IconButton onClick={() => void load()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" disabled={saving} onClick={() => void handleSave()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Lưu thay đổi'}
          </Button>
        </Stack>
      </Stack>

      {groups.map((group) => {
        const enabledCount = group.rows.filter((r) => isRowEnabled(r)).length;
        const allEnabled = enabledCount === group.rows.length;
        const noneEnabled = enabledCount === 0;
        return (
          <Accordion key={group.group} variant="outlined" sx={{ mb: 1.5 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%', pr: 1 }}
              >
                <Typography fontWeight={600}>{group.groupLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {enabledCount}/{group.rows.length} đang bật
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allEnabled}
                      indeterminate={!allEnabled && !noneEnabled}
                      onChange={(e) => toggleGroup(group, e.target.checked)}
                    />
                  }
                  label={<Typography fontWeight={600}>Chọn tất cả</Typography>}
                />
                <Box sx={{ pl: 3 }}>
                  <Stack spacing={0.5}>
                    {group.rows.map((row) => (
                      <FormControlLabel
                        key={key(row.entity_type, row.action)}
                        control={
                          <Checkbox checked={isRowEnabled(row)} onChange={() => toggleRow(row)} />
                        }
                        label={actionLabel(row.entity_type, row.action)}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {groups.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          Chưa có cấu hình nhật ký nào.
        </Paper>
      )}
    </Box>
  );
}
